/**
 * Clinic portal treatment report list hook.
 * API calls go through @/services/main/treatmentReport; errors surfaced via toast.
 */

import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { getTreatmentReportsList, type TreatmentReportListQueryParams } from '@/services/main/treatmentReport';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

export type TreatmentReportListFilters = TreatmentReportListQueryParams;

/**
 * Paginated treatment report list for a patient (tenant + clinic from auth context).
 */
export const useTreatmentReportList = (
  patientId?: string,
  filters?: TreatmentReportListQueryParams,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery({
    queryKey: ['treatment-reports', 'list', tenant, clinicNumber, patientId, serviceType, filters],
    queryFn: () => getTreatmentReportsList(tenant! || '', clinicNumber! || '', patientId!, serviceType, filters),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!patientId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

export default useTreatmentReportList;
